import { BadgeUnlockHint } from 'shared/components/BadgeComponent';
import { Badge } from 'features/catalog/useBadges';
import { CONTRIBUTION_AWARD_THRESHOLDS } from 'shared/hooks/getAutoUpdatedMap';
import {
  ANTON_LAST_AMONG_US_DATE,
  BADGE_DESCRIPTIONS,
  OFFSITE_TENURE_LADDER,
  offsiteStampBadgeIds,
  isAntonImpostorBannedByTenure,
  isGoldenHandoffBannedByTenure,
  isSoftHiddenBySiblingOffsite,
  isUnlockedFullTimeBannedByTenure,
  isWarmthWarriorBannedByTenure,
  NYC_COAT_DRIVE_2025_DATE,
  tenureGateForYearsSinceEvent,
} from 'features/catalog/badge-definitions';
import { BADGE_GROUPS } from 'features/catalog/badge-groups';
import { isBadgeReleased, unreleasedLockLine } from 'features/catalog/badge-release';
import { compareBadgesByScoreDesc } from 'features/catalog/rarity';

const TALOS_TENURE_GROUP = 'Tenure';

/** Lock line exactly like Anton’s Impostor: “🔒 Talos Tenure” + normal description; click scrolls to Tenure. */
const talosTenureLockHint = (opts?: { requiredBadgeIds?: string[] }): BadgeUnlockHint => ({
  lines: ['🔒 Talos Tenure'],
  hidesDescription: false,
  requiredBadgeIds: opts?.requiredBadgeIds,
  unlockClickTarget: { groupName: TALOS_TENURE_GROUP },
});

/** Unlock / lock hint for an unclaimed badge in edit mode. */
export const getExtraBadgeDescription = (
  badge: Badge,
  badgesMap: Map<string, Badge>,
  enabledBadgesMap: Map<string, boolean>,
  /** Unique idea+design contribution count — Badger / Badge Baron progress. */
  submittedBadgeCount = 0
): BadgeUnlockHint | null => {
  if (enabledBadgesMap.get(badge.id)) {
    return null;
  }

  if (badge.id === 'coi1' && isGoldenHandoffBannedByTenure(enabledBadgesMap)) {
    return talosTenureLockHint();
  }

  if (badge.id === 'intr' && isUnlockedFullTimeBannedByTenure(enabledBadgesMap)) {
    return talosTenureLockHint();
  }

  if (badge.id === 'xray' && isAntonImpostorBannedByTenure(enabledBadgesMap)) {
    const gate = tenureGateForYearsSinceEvent(ANTON_LAST_AMONG_US_DATE);
    return talosTenureLockHint({ requiredBadgeIds: gate?.ids });
  }

  if (badge.id === 'warm' && isWarmthWarriorBannedByTenure(enabledBadgesMap)) {
    const gate = tenureGateForYearsSinceEvent(NYC_COAT_DRIVE_2025_DATE);
    return talosTenureLockHint({ requiredBadgeIds: gate?.ids });
  }

  const releaseLine = unreleasedLockLine(badge.id);
  if (releaseLine) {
    return {
      lines: [releaseLine],
      hidesDescription: true,
    };
  }

  if (badge.banSelfClaim) {
    const contributionThreshold = CONTRIBUTION_AWARD_THRESHOLDS[badge.id];
    // Missing restricted deps still nudge on hover (e.g. Badge Baron → Badger in the hidden stack).
    const missingRestrictedIds = badge.restrictedRules?.ids.filter((id) => !enabledBadgesMap.get(id)) ?? [];
    const unmetRestricted =
      !!badge.restrictedRules &&
      badge.restrictedRules.ids.filter((id) => enabledBadgesMap.get(id)).length < badge.restrictedRules.count;
    const restrictedNudgeIds = unmetRestricted ? missingRestrictedIds : undefined;

    // Badger / Badge Baron: live contribution progress (same shape as Offices / Retreats).
    if (contributionThreshold != null) {
      const have = Math.min(submittedBadgeCount, contributionThreshold);
      return {
        lines: [`🔒 ${have}/${contributionThreshold} Contributions`],
        hidesDescription: false,
        requiredBadgeIds: restrictedNudgeIds,
      };
    }

    const rules = badge.autoClaimRules;
    const hint = badge.banSelfClaimHint?.trim();
    // Progress milestones (offices / retreats): show live x/N toward auto-claim.
    if (rules && rules.count > 1 && hint) {
      const owned = rules.ids.filter((id) => enabledBadgesMap.get(id)).length;
      const have = Math.min(owned, rules.count);
      return {
        lines: [`🔒 ${have}/${rules.count} ${hint}`],
        hidesDescription: false,
        requiredBadgeIds: rules.ids.filter((id) => !enabledBadgesMap.get(id)),
      };
    }
    return {
      lines: [`🔒 ${hint || 'No Self-claim.'}`],
      hidesDescription: false,
      requiredBadgeIds: restrictedNudgeIds,
    };
  }

  if (badge.restrictedRules) {
    const ownedBadges = badge.restrictedRules.ids.filter((id) => enabledBadgesMap.get(id));
    const hasMetRequirement = ownedBadges.length >= badge.restrictedRules.count;

    if (hasMetRequirement) {
      return null; // User has met the requirement, no "unlocks with" needed
    }

    const missingIds = badge.restrictedRules.ids.filter((id) => !enabledBadgesMap.get(id));

    // Offsite venue stamps / dated events: tenure gate → same “Talos Tenure” lock as Anton (description stays).
    const catalog = BADGE_DESCRIPTIONS.get(badge.id);
    if (catalog?.offsiteYear != null || catalog?.tenureGateEventDate) {
      return talosTenureLockHint({ requiredBadgeIds: missingIds });
    }

    const missingBadges = missingIds.map((id) => badgesMap.get(id)?.name);

    // Exactly one missing → link that badge. Any-of-N (e.g. offices / offsights) → +1 <group>.
    if (missingBadges.length === 1) {
      const name = missingBadges[0];
      return {
        lines: [`🔑 Requires:`],
        hidesDescription: true,
        requiredBadgeIds: missingIds,
        searchOnClickTargets: name ? [{ label: name, badgeId: missingIds[0] }] : undefined,
      };
    }

    if (badge.restrictedRules.count === 1 && badge.restrictedRules.ids.length > 1) {
      const groupName =
        BADGE_GROUPS.find((group) => badge.restrictedRules!.ids.every((id) => group.badgesIDs.includes(id)))?.name ??
        null;
      if (groupName) {
        return {
          lines: [`🔑 Requires:`],
          hidesDescription: true,
          requiredBadgeIds: missingIds,
          searchOnClickTargets: [{ label: `+1 ${groupName}`, groupName }],
        };
      }
    }
  }

  return null;
};

/**
 * Whether a badge belongs in the edit main grid vs claimed/hidden — evaluated against
 * a fixed ownership map (usually the profile baseline at edit open).
 */
export const isBadgeClaimableAgainstOwnership = (
  badge: Badge,
  badgesMap: Map<string, Badge>,
  ownershipMap: Map<string, boolean>,
  submittedBadgeCount: number
): boolean => {
  if (ownershipMap.get(badge.id)) return false;

  // Contribution / system awards are banSelfClaim (not “claimable”), but keep them as incentives:
  // Badger + QA Specialist always; Badge Baron once they have Badger territory (1+ contributions; award at 10).
  if (badge.id === '65ry' || badge.id === 'bpzs') return true;
  if (badge.id === 'uwpa' && submittedBadgeCount >= 1) return true;

  const lock = getExtraBadgeDescription(badge, badgesMap, ownershipMap, submittedBadgeCount);
  if (lock == null) return true;

  const rules = badge.autoClaimRules;
  if (!rules?.ids.length || !isBadgeReleased(badge.id)) return false;
  let ownedDeps = 0;
  let claimableDeps = 0;
  for (const depId of rules.ids) {
    if (ownershipMap.get(depId)) {
      ownedDeps += 1;
      continue;
    }
    const dep = badgesMap.get(depId);
    if (dep && getExtraBadgeDescription(dep, badgesMap, ownershipMap, submittedBadgeCount) == null) {
      claimableDeps += 1;
    }
  }
  return ownedDeps + claimableDeps >= rules.count;
};

/**
 * Edit-group rarity header: still-missing vs the saved profile, but only badges
 * on the main grid (claimable now, or claimed this session). Hidden / unreleased
 * stay out until they unhide mid-edit.
 */
export const isEditGroupRarityCountable = (
  badge: Badge,
  badgesMap: Map<string, Badge>,
  profileOwned: ReadonlySet<string>,
  liveOwned: Map<string, boolean>,
  submittedBadgeCount: number
): boolean => {
  if (profileOwned.has(badge.id)) return false;
  if (!liveOwned.get(badge.id) && isSoftHiddenBySiblingOffsite(badge.id, profileOwned)) return false;
  if (liveOwned.get(badge.id)) return true;
  return isBadgeClaimableAgainstOwnership(badge, badgesMap, liveOwned, submittedBadgeCount);
};

const NEW_PIXEL_ID = 'eaze';

const isSingleParentRule = (rules?: { count: number; ids: string[] }): rules is { count: number; ids: string[] } =>
  rules != null && rules.count === 1 && rules.ids.length === 1;

/**
 * Linear upgrade ladders (Event Enthusiast → Maestro → Legend): unique
 * single-parent `restrictedRules` chains. Shared gates (1-year, an office)
 * have many children and are left as direct neighbors only.
 * Tenure rungs are one ladder even though Forest is also a shared gate.
 * Offsite stamps are a clique only when viewing a stamp — a year event
 * (Olympics Winner → Woodloch 2024) must not pull every other retreat.
 */
const upgradeGroupIds = (startId: string, badgesMap: Map<string, Badge>): Set<string> => {
  const offsiteStampSet = new Set(offsiteStampBadgeIds());
  const childrenOf = new Map<string, string[]>();
  for (const other of badgesMap.values()) {
    if (other.id === NEW_PIXEL_ID || !isSingleParentRule(other.restrictedRules)) continue;
    const parent = other.restrictedRules.ids[0];
    if (parent === NEW_PIXEL_ID) continue;
    const list = childrenOf.get(parent);
    if (list) list.push(other.id);
    else childrenOf.set(parent, [other.id]);
  }
  const adj = new Map<string, Set<string>>();
  const link = (a: string, b: string) => {
    if (a === b) return;
    let from = adj.get(a);
    if (!from) {
      from = new Set();
      adj.set(a, from);
    }
    from.add(b);
    let to = adj.get(b);
    if (!to) {
      to = new Set();
      adj.set(b, to);
    }
    to.add(a);
  };
  for (const [parent, children] of childrenOf) {
    if (children.length !== 1) continue;
    if (offsiteStampSet.has(parent)) continue;
    link(parent, children[0]);
  }
  if (OFFSITE_TENURE_LADDER.some((rung) => rung.id === startId)) {
    for (let i = 0; i < OFFSITE_TENURE_LADDER.length; i += 1) {
      for (let j = i + 1; j < OFFSITE_TENURE_LADDER.length; j += 1) {
        link(OFFSITE_TENURE_LADDER[i].id, OFFSITE_TENURE_LADDER[j].id);
      }
    }
  }
  if (offsiteStampSet.has(startId)) {
    const offsiteStamps = [...offsiteStampSet];
    for (let i = 0; i < offsiteStamps.length; i += 1) {
      for (let j = i + 1; j < offsiteStamps.length; j += 1) {
        link(offsiteStamps[i], offsiteStamps[j]);
      }
    }
  }
  const group = new Set<string>([startId]);
  const queue = [startId];
  while (queue.length > 0) {
    const id = queue.pop() as string;
    for (const next of adj.get(id) ?? []) {
      if (group.has(next)) continue;
      group.add(next);
      queue.push(next);
    }
  }
  group.delete(startId);
  return group;
};

/**
 * Requires / auto-claim / thematic neighbors, plus the rest of an upgrade ladder.
 * New Pixel auto-claims from every other badge — never surface it (or its catalog-wide deps).
 */
export const relatedBadgesFor = (badge: Badge, badgesMap: Map<string, Badge>): Badge[] => {
  const ids = new Set<string>();
  for (const id of badge.restrictedRules?.ids ?? []) ids.add(id);
  if (badge.id !== NEW_PIXEL_ID) {
    for (const id of badge.autoClaimRules?.ids ?? []) ids.add(id);
  }
  for (const id of badge.relatedIds ?? []) ids.add(id);
  for (const id of upgradeGroupIds(badge.id, badgesMap)) ids.add(id);
  for (const other of badgesMap.values()) {
    if (other.id === badge.id || other.id === NEW_PIXEL_ID) continue;
    if (other.restrictedRules?.ids.includes(badge.id) || other.autoClaimRules?.ids.includes(badge.id)) {
      ids.add(other.id);
    }
    if (other.relatedIds?.includes(badge.id)) ids.add(other.id);
  }
  ids.delete(badge.id);
  ids.delete(NEW_PIXEL_ID);
  const related: Badge[] = [];
  for (const id of ids) {
    const next = badgesMap.get(id);
    if (next) related.push(next);
  }
  related.sort((a, b) => compareBadgesByScoreDesc(a, b) || (a.name ?? '').localeCompare(b.name ?? ''));
  return related;
};
